import JMuxer from "jmuxer";
import {
  ArrowLeft,
  Home,
  Keyboard,
  Lock,
  Maximize2,
  Power,
  RotateCcw,
  ScanText,
  Volume1,
  Volume2,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { tokenStore } from "../api/client";
import { getPhone, ocrSnapshot, rebootPhone, type OCRItem, type Phone } from "../api/phones";
import { StatusBadge } from "../components/StatusBadge";

interface ControlMessage {
  type: "tap" | "swipe" | "key" | "type_text" | "key_action";
  // tap : x_norm/y_norm + duration_ms optionnel (long-press si >= 400ms)
  x_norm?: number;
  y_norm?: number;
  // swipe : from/to + duration_ms (durée totale, ~human range 150..1500ms)
  from?: { x: number; y: number };
  to?: { x: number; y: number };
  duration_ms?: number;
  keycode?: "home" | "lock" | "power" | "volume_up" | "volume_down";
  // Pour les keycodes : nb de clics rapides (1=single, 2=double, …). Default 1.
  // Géré côté tweak via injectConsumerUsagePage:clickCount:.
  count?: number;
  // type_text : string UTF-8 (déjà filtrée des emoji côté frontend).
  text?: string;
  // key_action : "backspace" | "enter" | "tab"
  key?: "backspace" | "enter" | "tab";
}

// ---- Filtrage emoji et caractères de contrôle ----
// Match :
//   - emoji et symboles unicode dans les plages SMP (\u{1F000}..\u{1FFFF})
//     et symboles "Misc Symbols and Pictographs" (\u{2600}..\u{27BF})
//   - variation selectors VS1..VS16 (\uFE00..\uFE0F) et ZWJ (\u200D) qui
//     composent les emoji multi-codepoints (skin tones, etc.)
//   - control chars ASCII (\u0000..\u001F + \u007F)
// On preserve \n et \t qu'on traite côté key_action séparément.
const EMOJI_REGEX = /[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\uFE00-\uFE0F\u200D]/gu;
const CTRL_REGEX = /[\u0000-\u001F\u007F]/g;
function sanitizeText(input: string): string {
  return input.replace(EMOJI_REGEX, "").replace(CTRL_REGEX, "");
}

// ---- Gesture detector seuils ----
// kTapMaxDistance : un mouvement < 2.5% du plus grand côté du conteneur est
//   considéré comme "stationnaire" (= un tap ou un long-press). 2.5% c'est
//   environ 20px sur un device de 800px, ce qui tolère le tremblement de la
//   souris sans dériver vers un swipe involontaire.
// kLongTapMinDuration : 400ms est le seuil iOS standard pour déclencher le
//   menu contextuel / l'app jiggle / la sélection texte longue.
// kSwipeMinDistance : un mouvement >= 4% du conteneur est considéré comme un
//   swipe explicite. Entre kTapMaxDistance et kSwipeMinDistance c'est la zone
//   morte où on considère qu'on a un tap (l'utilisateur a bougé un peu sans
//   intention de swiper).
const kTapMaxDistance     = 0.025;
const kSwipeMinDistance   = 0.04;
const kLongTapMinDuration = 400; // ms

interface GestureState {
  startedAt: number;
  startX: number;     // [0..1] normalisé conteneur
  startY: number;
  lastX: number;
  lastY: number;
  maxDelta: number;   // distance euclidienne max depuis (startX, startY), normalisée
  // Pointer id (Pointer Events API) : nous permet de filtrer les pointer
  // secondaires (multi-touch, stylet) — on ne garde que le 1er doigt qui
  // a touché l'écran pour rester simple.
  pointerId: number;
}

// ---- Backoff exponentiel pour la reconnexion WS ----
// Step (ms) : 0, 500, 1500, 3000, 6000, 12000, 20000, puis cap.
// On garde une borne haute (20 s) pour ne pas être trop agressif si le tel
// est down, mais assez court pour récupérer vite quand le réseau revient.
function reconnectDelay(attempt: number): number {
  const base = Math.min(20000, 500 * Math.pow(2, Math.max(0, attempt - 1)));
  // jitter ±20% pour éviter le thundering herd si plusieurs onglets reconnectent ensemble.
  return base * (0.8 + Math.random() * 0.4);
}

export function PhoneStreamPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [phone, setPhone] = useState<Phone | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [streamStatus, setStreamStatus] = useState<"connecting" | "streaming" | "error" | "closed">(
    "connecting"
  );
  const [streamMessage, setStreamMessage] = useState<string>("Connexion au tel…");
  const [controlConnected, setControlConnected] = useState(false);
  // Ratio réel de la vidéo, mesuré dès qu'on a les metadata du flux H.264.
  // Default 9/16 (iPhone 7/8 = 750x1334) avant onloadedmetadata. Le container
  // parent utilise ce ratio pour que le frame device "moule" la vidéo sans
  // bandes noires, quel que soit le modèle (iPhone 8, 14 Pro, iPad, etc.).
  const [videoAspect, setVideoAspect] = useState<string>("9 / 16");

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const muxerRef = useRef<JMuxer | null>(null);
  const streamWsRef = useRef<WebSocket | null>(null);
  const controlWsRef = useRef<WebSocket | null>(null);
  // Buffer de frappe : on accumule les caractères imprimables et on les envoie
  // en un seul `type_text` (flush). Chaque type_text déclenche côté tweak une
  // détection OCR du layer clavier (~plusieurs centaines de ms), donc envoyer
  // un message par caractère serait inutilisable. On flush sur idle, sur une
  // touche spéciale (Backspace/Enter/Tab), sur paste, et au démontage.
  const typeBufferRef = useRef<string>("");
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Charge le phone ----
  useEffect(() => {
    if (!id) return;
    let cancel = false;
    (async () => {
      try {
        const p = await getPhone(id);
        if (!cancel) setPhone(p);
      } catch (e) {
        if (!cancel) setError(e instanceof Error ? e.message : "Erreur de chargement");
      }
    })();
    return () => {
      cancel = true;
    };
  }, [id]);

  // ---- WS stream + JMuxer + auto-reconnect ----
  // Dépend de `phone` (pas seulement `id`) : tant que phone n'est pas chargé,
  // la page rend `<div>Chargement…</div>` et le <video> n'existe pas dans le
  // DOM → videoRef.current === null. Sans `phone` en dep, l'effect ne re-run
  // pas après chargement de phone et le WS ne s'ouvre jamais.
  //
  // Reconnect pattern : sur close non-voulu (`destroyed=false`), on schedule
  // un nouveau connect avec backoff exponentiel. On recrée aussi le JMuxer
  // (sa MediaSource est dans un état terminé après un close du flux). Les
  // event listeners visibility/online forcent un reconnect immédiat si le
  // WS est dead quand l'onglet revient au premier plan / le réseau remonte.
  useEffect(() => {
    if (!id || !phone) return;
    const video = videoRef.current;
    if (!video) {
      console.warn("[stream] videoRef not ready, abandon");
      return;
    }

    const token = tokenStore.get();
    if (!token) {
      setStreamStatus("error");
      setStreamMessage("Pas de session : reconnecte-toi sur /login");
      return;
    }

    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const streamUrl = `${proto}://${window.location.host}/api/phones/${id}/stream?token=${encodeURIComponent(token)}`;

    let destroyed = false;
    let attempt = 0;
    let reconnectTimer: number | null = null;
    let currentWs: WebSocket | null = null;
    let currentMuxer: JMuxer | null = null;

    const onMeta = () => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (w > 0 && h > 0) {
        setVideoAspect(`${w} / ${h}`);
        console.info(`[stream] video resolution ${w}x${h}`);
      }
    };
    video.addEventListener("loadedmetadata", onMeta);
    video.addEventListener("resize", onMeta);

    const teardownConnection = () => {
      if (currentWs) {
        try { currentWs.onclose = null; } catch (_) { /* ignore */ }
        try { currentWs.close(); } catch (_) { /* ignore */ }
        currentWs = null;
      }
      if (currentMuxer) {
        try { currentMuxer.destroy(); } catch (_) { /* ignore */ }
        currentMuxer = null;
      }
      muxerRef.current = null;
      streamWsRef.current = null;
    };

    const scheduleReconnect = () => {
      if (destroyed || reconnectTimer !== null) return;
      const delay = reconnectDelay(attempt);
      setStreamStatus("connecting");
      setStreamMessage(
        `Reconnexion (tentative ${attempt + 1}) dans ${Math.round(delay / 1000)}s…`
      );
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (destroyed) return;
      teardownConnection();
      attempt += 1;

      console.info(
        `[stream] opening ${streamUrl.replace(token, "<jwt>")} (tentative ${attempt})`
      );
      setStreamStatus("connecting");
      setStreamMessage(attempt === 1 ? "Connexion au tel…" : `Reconnexion (tentative ${attempt})…`);

      const muxer = new JMuxer({
        node: video,
        mode: "video",
        flushingTime: 0,
        fps: 30,
        debug: false,
        onError: (data: unknown) => {
          console.warn("[stream] JMuxer error", data);
        },
      });
      currentMuxer = muxer;
      muxerRef.current = muxer;

      const ws = new WebSocket(streamUrl);
      ws.binaryType = "arraybuffer";
      currentWs = ws;
      streamWsRef.current = ws;

      ws.onopen = () => {
        setStreamStatus("connecting");
        setStreamMessage("WebSocket ouvert, attente du tel…");
      };

      ws.onmessage = (e) => {
        if (typeof e.data === "string") {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === "stream_started") {
              attempt = 0;
              setStreamStatus("streaming");
              setStreamMessage(`Source : ${msg.source}`);
            } else if (msg.type === "error") {
              setStreamStatus("error");
              setStreamMessage(msg.error);
            }
          } catch (_) { /* ignore non-JSON */ }
        } else {
          try {
            muxer.feed({ video: new Uint8Array(e.data as ArrayBuffer) });
          } catch (err) {
            console.warn("[stream] muxer feed error", err);
          }
        }
      };

      ws.onerror = () => {
        setStreamStatus("error");
        setStreamMessage("Erreur WebSocket — retry…");
      };

      ws.onclose = (ev) => {
        if (destroyed) return;
        console.info(`[stream] WS closed code=${ev.code} reason=${ev.reason}`);
        setStreamStatus("closed");
        setStreamMessage((m) =>
          m && !m.startsWith("Connexion") ? m : "Stream fermé — retry…"
        );
        scheduleReconnect();
      };
    };

    // Forçage de reconnexion si le WS est dead quand l'onglet revient ou
    // quand le réseau remonte. On évite de laisser le backoff de 20s en
    // attente dans ces cas (le retour réseau est un signal fort).
    const forceReconnectIfNeeded = (reason: string) => {
      if (destroyed) return;
      if (currentWs && currentWs.readyState === WebSocket.OPEN) return;
      console.info(`[stream] force reconnect (${reason})`);
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      // reset partiel du compteur : on réessaye agressivement (1ère tentative
      // = 500ms), mais on ne réinitialise PAS à 0 pour éviter les boucles si
      // le close vient effectivement d'un blocage réseau sur cet onglet.
      attempt = Math.max(0, attempt - 1);
      connect();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") forceReconnectIfNeeded("visibilitychange");
    };
    const onOnline = () => forceReconnectIfNeeded("online");

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);

    connect();

    return () => {
      destroyed = true;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("resize", onMeta);
      teardownConnection();
    };
  }, [id, phone]);

  // ---- WS control (touch + boutons système) avec auto-reconnect ----
  // Mêmes principes que le stream : backoff exponentiel + visibility/online.
  // Beaucoup plus léger (juste du JSON), donc tentatives plus agressives.
  useEffect(() => {
    if (!id) return;
    const token = tokenStore.get();
    if (!token) return;

    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const controlUrl = `${proto}://${window.location.host}/api/phones/${id}/control?token=${encodeURIComponent(token)}`;

    let destroyed = false;
    let attempt = 0;
    let reconnectTimer: number | null = null;
    let currentWs: WebSocket | null = null;

    const connect = () => {
      if (destroyed) return;
      attempt += 1;
      const ws = new WebSocket(controlUrl);
      currentWs = ws;
      controlWsRef.current = ws;

      ws.onopen = () => {
        attempt = 0;
        setControlConnected(true);
      };
      ws.onerror = () => setControlConnected(false);
      ws.onclose = () => {
        if (destroyed) return;
        setControlConnected(false);
        if (reconnectTimer !== null) return;
        const delay = reconnectDelay(attempt);
        reconnectTimer = window.setTimeout(() => {
          reconnectTimer = null;
          connect();
        }, delay);
      };
    };

    const forceReconnectIfNeeded = () => {
      if (destroyed) return;
      if (currentWs && currentWs.readyState === WebSocket.OPEN) return;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      attempt = Math.max(0, attempt - 1);
      connect();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") forceReconnectIfNeeded();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", forceReconnectIfNeeded);

    connect();

    return () => {
      destroyed = true;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", forceReconnectIfNeeded);
      try { if (currentWs) currentWs.close(); } catch (_) { /* ignore */ }
      controlWsRef.current = null;
    };
  }, [id]);

  const sendControl = useCallback((msg: ControlMessage) => {
    const ws = controlWsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify(msg));
    } catch (_) { /* ignore */ }
  }, []);

  const sendKey = useCallback(
    (keycode: NonNullable<ControlMessage["keycode"]>, count = 1) => {
      sendControl({ type: "key", keycode, count });
    },
    [sendControl]
  );

  // Flush du buffer de frappe : envoie le texte accumulé en un seul type_text.
  const flushTypeBuffer = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const text = typeBufferRef.current;
    if (text.length === 0) return;
    typeBufferRef.current = "";
    sendControl({ type: "type_text", text });
  }, [sendControl]);

  // ---- Capture clavier PC → tel iOS (frappe sur le clavier iOS via le tweak) ----
  //
  // On capture les keydown au niveau document. On ignore quand le focus
  // est dans un input du dashboard (search bar, etc.) pour ne pas envoyer
  // au tel ce que tape l'admin dans son propre UI.
  //
  // Les caractères imprimables sont ACCUMULÉS dans un buffer puis envoyés en
  // un seul type_text. Raison : côté tweak, chaque type_text refait une
  // détection OCR du layer clavier (~1.5-2s) — un message par caractère
  // rendrait la frappe inutilisable. En batchant, l'OCR n'a lieu qu'une fois
  // par lot.
  //
  // Deux déclencheurs de flush :
  //   1. Idle : l'utilisateur arrête de taper ~200ms → on envoie.
  //   2. Seuil : le buffer atteint FLUSH_MAX_CHARS → on envoie tout de suite
  //      sans attendre l'idle (frappe continue/rapide ou gros collage manuel),
  //      pour éviter d'accumuler une chaîne énorme dans un seul type_text.
  //
  // Les touches spéciales (Backspace/Enter/Tab) et le paste flush d'abord le
  // buffer pour préserver l'ordre, puis envoient leur propre commande.
  useEffect(() => {
    function isDashboardInput(el: EventTarget | null): boolean {
      if (!el || !(el instanceof Element)) return false;
      const tag = el.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (el instanceof HTMLElement && el.isContentEditable) return true;
      return false;
    }

    // Debounce : 200ms d'inactivité clavier déclenche l'envoi du buffer.
    const FLUSH_IDLE_MS = 400;
    // Flush anticipé : au-delà de 40 caractères accumulés, on n'attend plus
    // l'idle (sinon une frappe soutenue ferait grossir le buffer sans limite).
    const FLUSH_MAX_CHARS = 40;
    function scheduleFlush() {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(flushTypeBuffer, FLUSH_IDLE_MS);
    }

    function onKeyDown(e: KeyboardEvent) {
      // Skip si focus dans un input du dashboard.
      if (isDashboardInput(e.target)) return;
      // Skip si le canal control n'est pas prêt (pas de bruit inutile).
      const ws = controlWsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;

      // Ctrl+V est géré par le handler `paste` séparé (browser fournit
      // le clipboardData, plus fiable que de re-récupérer ici).
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v") return;

      // Touches spéciales : on flush le buffer AVANT pour garder l'ordre, puis
      // on envoie la commande dédiée.
      if (e.key === "Backspace") {
        e.preventDefault();
        flushTypeBuffer();
        sendControl({ type: "key_action", key: "backspace" });
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        flushTypeBuffer();
        sendControl({ type: "key_action", key: "enter" });
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        flushTypeBuffer();
        sendControl({ type: "key_action", key: "tab" });
        return;
      }

      // Touches "métadonnées" (flèches, F1..F12, Escape, Home/End, etc.) :
      // e.key fait 1 caractère pour les imprimables, plus que 1 pour les autres.
      if (e.key.length !== 1) return;

      // Combos avec Ctrl/Cmd/Alt : on laisse passer au browser (sélection,
      // copy, undo, etc.) sans relayer au tel.
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const sanitized = sanitizeText(e.key);
      if (sanitized.length === 0) return;

      e.preventDefault();
      typeBufferRef.current += sanitized;
      // Seuil atteint → envoi immédiat ; sinon on (re)programme l'idle flush.
      if (typeBufferRef.current.length >= FLUSH_MAX_CHARS) {
        flushTypeBuffer();
      } else {
        scheduleFlush();
      }
    }

    function onPaste(e: ClipboardEvent) {
      if (isDashboardInput(e.target)) return;
      const ws = controlWsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;

      const raw = e.clipboardData?.getData("text/plain") ?? "";
      const cleaned = sanitizeText(raw);
      if (cleaned.length === 0) return;

      e.preventDefault();
      // Flush le buffer en attente puis ajoute le paste (préserve l'ordre).
      typeBufferRef.current += cleaned;
      flushTypeBuffer();
      console.info(`[keyboard] paste ${cleaned.length} chars`);
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("paste", onPaste);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("paste", onPaste);
      // Démontage : flush ce qui reste pour ne rien perdre.
      flushTypeBuffer();
    };
  }, [sendControl, flushTypeBuffer]);

  // ---- Gesture detector : tap / long-tap / swipe ----
  // Implémenté avec les Pointer Events (couvre souris + touchscreen + stylet
  // d'un seul handler). On track UN SEUL pointer à la fois (le 1er qui touche)
  // pour ne pas avoir à gérer le pinch/zoom (qui n'est pas mappable à un
  // VFHIDInjector single-finger de toute façon).
  const gestureRef = useRef<GestureState | null>(null);
  const [pressing, setPressing] = useState(false); // feedback visuel (ring)

  // ---- État OCR debug ----
  // ocrItems = liste des textes détectés avec bboxes [0..1]. null = pas encore
  // capturé (overlay fermé). [] = capturé mais 0 détecté (overlay visible mais
  // vide). Une bbox est cliquable : copy le texte au clipboard (debug pour
  // construire des wait_for_text/tap_text dans le script JSON).
  const [ocrItems, setOcrItems] = useState<OCRItem[] | null>(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState<string | null>(null);
  // Timestamp de la dernière capture, pour rappeler au user que les bboxes
  // peuvent être désynchro avec le flux live si l'écran a bougé entre temps.
  const [ocrCapturedAt, setOcrCapturedAt] = useState<number | null>(null);

  const runOcr = useCallback(async () => {
    if (!id || ocrLoading) return;
    setOcrLoading(true);
    setOcrError(null);
    try {
      const items = await ocrSnapshot(id);
      setOcrItems(items);
      setOcrCapturedAt(Date.now());
      console.info(`[ocr] ${items.length} items detected`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "OCR error";
      setOcrError(msg);
      setOcrItems(null);
      console.warn("[ocr] error:", msg);
    } finally {
      setOcrLoading(false);
    }
  }, [id, ocrLoading]);

  const closeOcr = useCallback(() => {
    setOcrItems(null);
    setOcrError(null);
    setOcrCapturedAt(null);
  }, []);

  // Convertit un PointerEvent en coords normalisées [0..1] dans le frame du video.
  const normalizePointer = useCallback(
    (e: React.PointerEvent<HTMLElement>): { x: number; y: number } => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
      return { x, y };
    },
    []
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      // Bouton gauche (souris) uniquement. e.button === 0 OK, autres = ignore.
      // (Pour les touch / pen, e.button vaut 0 par défaut, donc pas de souci.)
      if (e.button !== 0) return;
      if (gestureRef.current) return; // déjà un geste en cours, ignore
      const { x, y } = normalizePointer(e);
      gestureRef.current = {
        startedAt: performance.now(),
        startX: x,
        startY: y,
        lastX: x,
        lastY: y,
        maxDelta: 0,
        pointerId: e.pointerId,
      };
      setPressing(true);
      // Capture le pointer pour recevoir move/up même si le curseur sort du
      // <video>. Sans ça, un drag rapide qui sort de la zone perd l'event up.
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
    },
    [normalizePointer]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const g = gestureRef.current;
      if (!g || g.pointerId !== e.pointerId) return;
      const { x, y } = normalizePointer(e);
      g.lastX = x;
      g.lastY = y;
      const dx = x - g.startX;
      const dy = y - g.startY;
      const d  = Math.sqrt(dx * dx + dy * dy);
      if (d > g.maxDelta) g.maxDelta = d;
    },
    [normalizePointer]
  );

  const handlePointerEnd = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const g = gestureRef.current;
      if (!g || g.pointerId !== e.pointerId) return;
      gestureRef.current = null;
      setPressing(false);
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }

      // Position finale (peut être hors-conteneur si le pointer est sorti via
      // capture ; on clamp [0..1] dans normalizePointer pour rester safe).
      const { x: x1, y: y1 } = normalizePointer(e);
      const x0 = g.startX;
      const y0 = g.startY;
      const dx = x1 - x0;
      const dy = y1 - y0;
      const finalDelta = Math.sqrt(dx * dx + dy * dy);
      const dur = performance.now() - g.startedAt;

      // Decision tree :
      // 1) max delta ≥ kSwipeMinDistance → SWIPE (du start à la position finale)
      // 2) max delta < kTapMaxDistance et durée ≥ kLongTapMinDuration → LONG-TAP
      // 3) sinon → TAP simple
      if (g.maxDelta >= kSwipeMinDistance) {
        // duration_ms : si le user a swipé lentement, on garde la durée
        // observée (jusqu'à 1500ms). Sinon on met un default 250ms qui produit
        // un swipe naturel côté iOS.
        const durationMs = Math.max(150, Math.min(1500, Math.round(dur)));
        sendControl({
          type: "swipe",
          from: { x: x0, y: y0 },
          to:   { x: x1, y: y1 },
          duration_ms: durationMs,
        });
        console.info(
          `[gesture] swipe (${x0.toFixed(3)},${y0.toFixed(3)})→(${x1.toFixed(3)},${y1.toFixed(3)}) ${durationMs}ms (delta=${finalDelta.toFixed(3)})`
        );
      } else if (g.maxDelta < kTapMaxDistance && dur >= kLongTapMinDuration) {
        // Long-press : on envoie un control:tap avec duration_ms = dur observée
        // (le tweak v123+ honore ce champ). Sur les vieilles versions du tweak
        // qui ignorent duration_ms, ça dégrade en tap simple — fonctionnel mais
        // sans menu contextuel iOS.
        const durationMs = Math.min(5000, Math.max(kLongTapMinDuration, Math.round(dur)));
        sendControl({ type: "tap", x_norm: x0, y_norm: y0, duration_ms: durationMs });
        console.info(`[gesture] long-tap @(${x0.toFixed(3)},${y0.toFixed(3)}) ${durationMs}ms`);
      } else {
        sendControl({ type: "tap", x_norm: x0, y_norm: y0 });
        console.info(`[gesture] tap @(${x0.toFixed(3)},${y0.toFixed(3)})`);
      }
    },
    [sendControl, normalizePointer]
  );

  const handlePointerCancel = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const g = gestureRef.current;
    if (!g || g.pointerId !== e.pointerId) return;
    // OS a interrompu le geste (ex : iOS edge-swipe natif). On drop sans
    // envoyer pour ne pas faire un "fake tap" sur un cancel.
    gestureRef.current = null;
    setPressing(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
  }, []);

  const handleFullscreen = () => {
    if (videoRef.current?.requestFullscreen) {
      void videoRef.current.requestFullscreen();
    }
  };

  const handleReboot = async () => {
    if (!id) return;
    if (!window.confirm("Redémarrer ce téléphone ?")) return;
    try {
      const r = await rebootPhone(id);
      window.alert(r.instant ? "Reboot envoyé instantanément" : "Reboot mis en file (prochain heartbeat)");
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Erreur reboot");
    }
  };

  if (error) return <div className="p-8 text-red-400">{error}</div>;
  if (!phone) return <div className="p-8 text-ink-500">Chargement…</div>;

  return (
    <div className="h-screen flex flex-col bg-black">
      <div className="px-6 py-3 border-b border-ink-700 bg-ink-800/60 flex items-center gap-4">
        <button
          onClick={() => navigate("/phones")}
          className="p-2 rounded-md text-ink-400 hover:bg-ink-700/50 hover:text-white transition-colors"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-lg font-semibold text-white">{phone.name}</h1>
            <StatusBadge status={phone.computed_status} />
            <span className="text-xs text-ink-500 font-mono">{phone.connection_url}</span>
          </div>
          <div className="text-xs text-ink-500 mt-0.5 flex items-center gap-4">
            <span>{phone.model ?? "modèle inconnu"}{phone.spoofed_model ? ` → ${phone.spoofed_model}` : ""}</span>
            <span>iOS {phone.os_version ?? "?"}</span>
            <span className={`flex items-center gap-1 ${controlConnected ? "text-emerald-400" : "text-ink-500"}`}>
              {controlConnected ? <Wifi className="size-3" /> : <WifiOff className="size-3" />}
              control {controlConnected ? "OK" : "down"}
            </span>
            {controlConnected && (
              <span
                className="flex items-center gap-1 text-velvet-300"
                title="Clique d'abord dans un champ texte sur le tel (clavier iOS visible), puis tape — le texte est envoyé par petits lots. Ctrl+V pour coller depuis le presse-papiers."
              >
                <Keyboard className="size-3" />
                clavier → {phone.name}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void runOcr()}
            disabled={ocrLoading}
            title="Capture OCR : affiche en overlay tous les textes détectés par Vision iOS"
            className={
              "flex items-center gap-2 px-3 py-2 rounded-lg text-white text-sm transition-colors " +
              (ocrLoading
                ? "bg-violet-800 cursor-wait"
                : ocrItems !== null
                  ? "bg-violet-500 hover:bg-violet-400"
                  : "bg-violet-600 hover:bg-violet-500")
            }
          >
            <ScanText className="size-4" />
            {ocrLoading ? "OCR…" : ocrItems !== null ? `OCR (${ocrItems.length})` : "OCR"}
          </button>
          <button
            onClick={handleFullscreen}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-ink-700 hover:bg-ink-600 text-white text-sm transition-colors"
          >
            <Maximize2 className="size-4" />
            Plein écran
          </button>
          <button
            onClick={() => void handleReboot()}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm transition-colors"
          >
            <RotateCcw className="size-4" />
            Reboot
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 relative min-h-0 overflow-hidden">
        <div
          className="relative h-full max-w-full"
          style={{ aspectRatio: videoAspect }}
        >
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerCancel}
            // touch-none : désactive le pan/zoom natif du browser sur mobile
            // dev, sinon un swipe sur le video bouge la page au lieu d'envoyer
            // un control:swipe. select-none : empêche la sélection de texte
            // qui peut se déclencher sur un long-press souris.
            style={{ touchAction: "none", userSelect: "none" }}
            className={
              "absolute inset-0 w-full h-full object-contain rounded-2xl border-4 shadow-2xl bg-black cursor-crosshair transition-colors " +
              (pressing ? "border-emerald-500/70" : "border-ink-800")
            }
          />

          {/* Overlay OCR : couvre exactement le frame device (mêmes coords
              normalisées [0..1]). pointer-events-none sur le wrapper, mais
              chaque bbox individuelle remet pointer-events:auto pour être
              cliquable. Le `<video>` en dessous garde donc le focus pour les
              gestures sur les zones sans bbox. */}
          {ocrItems !== null && (
            <div className="absolute inset-0 pointer-events-none rounded-2xl overflow-hidden">
              {/* Bandeau d'info en haut */}
              <div className="absolute top-0 left-0 right-0 bg-violet-600/90 text-white text-xs px-3 py-1.5 flex items-center justify-between pointer-events-auto">
                <span>
                  OCR : {ocrItems.length} item{ocrItems.length === 1 ? "" : "s"}
                  {ocrCapturedAt != null && (
                    <span className="text-violet-200 ml-2">
                      · capturé il y a {Math.round((Date.now() - ocrCapturedAt) / 1000)}s
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => void runOcr()}
                    disabled={ocrLoading}
                    className="px-2 py-0.5 rounded bg-violet-500 hover:bg-violet-400 transition-colors disabled:cursor-wait"
                  >
                    {ocrLoading ? "…" : "Refresh"}
                  </button>
                  <button
                    onClick={closeOcr}
                    className="p-1 rounded hover:bg-violet-500 transition-colors"
                    title="Fermer overlay OCR"
                  >
                    <X className="size-3" />
                  </button>
                </div>
              </div>

              {/* Bboxes individuelles. Au-delà de ~80 items le rendu reste
                  fluide grâce à Tailwind + position absolute (pas de réflow
                  hors du container). */}
              {ocrItems.map((it, i) => (
                <OcrBoxOverlay key={i} item={it} />
              ))}
            </div>
          )}

          {/* Erreur OCR : message centré, fermable */}
          {ocrError && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-red-900/90 px-4 py-3 rounded-lg text-sm max-w-sm pointer-events-auto">
                <div className="font-semibold text-red-100">OCR erreur</div>
                <div className="text-xs text-red-200 mt-1">{ocrError}</div>
                <button
                  onClick={() => setOcrError(null)}
                  className="mt-2 px-2 py-1 rounded bg-red-700 hover:bg-red-600 text-xs"
                >
                  OK
                </button>
              </div>
            </div>
          )}

          {streamStatus !== "streaming" && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-black/80 px-6 py-4 rounded-xl text-center max-w-sm">
                <div className="text-sm text-ink-300 mb-2">
                  {streamStatus === "connecting" && (
                    <span className="inline-flex items-center gap-2">
                      <Power className="size-4 animate-pulse" />
                      Connexion…
                    </span>
                  )}
                  {streamStatus === "error" && (
                    <span className="text-red-300">⚠ Stream indisponible</span>
                  )}
                  {streamStatus === "closed" && (
                    <span className="text-ink-400">Stream fermé</span>
                  )}
                </div>
                <div className="text-xs text-ink-500">{streamMessage}</div>
                {streamStatus === "error" && (
                  <div className="text-xs text-ink-600 mt-3">
                    Le tweak iOS doit exposer un serveur TCP H.264 sur le port 8889.
                    Voir <code className="text-ink-400">HUB_INTEGRATION.md §10</code>.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Barre de boutons système : reproduit l'ancien panel (Home, double-Home,
          Lock, Volume +/-). Tous les events passent par le WS control →
          forward direct vers VFHIDInjector côté tweak (HID consumer page 0x0C). */}
      <div className="px-6 py-3 border-t border-ink-700 bg-ink-800/60 flex items-center justify-center gap-3 flex-wrap">
        <SystemKeyButton
          icon={<Home className="size-4" />}
          label="Home"
          disabled={!controlConnected}
          onClick={() => sendKey("home", 1)}
        />
        <SystemKeyButton
          icon={
            <span className="relative">
              <Home className="size-4" />
              <span className="absolute -top-1.5 -right-2 text-[10px] font-bold text-amber-300">
                ×2
              </span>
            </span>
          }
          label="App switcher"
          disabled={!controlConnected}
          onClick={() => sendKey("home", 2)}
        />
        <SystemKeyButton
          icon={<Lock className="size-4" />}
          label="Lock"
          disabled={!controlConnected}
          onClick={() => sendKey("lock", 1)}
        />
        <div className="w-px h-6 bg-ink-700" />
        <SystemKeyButton
          icon={<Volume2 className="size-4" />}
          label="Vol +"
          disabled={!controlConnected}
          onClick={() => sendKey("volume_up", 1)}
        />
        <SystemKeyButton
          icon={<Volume1 className="size-4" />}
          label="Vol −"
          disabled={!controlConnected}
          onClick={() => sendKey("volume_down", 1)}
        />
      </div>

      <div className="px-6 py-2 border-t border-ink-700 bg-ink-800/60 text-xs text-ink-500 text-center">
        Click = tap · maintenir &gt; 400ms = long-press · drag = swipe humain · coords normalisées [0..1]
      </div>
    </div>
  );
}

// Une bbox OCR cliquable. Click = copy text au clipboard pour faciliter la
// construction de scripts ({"type":"wait_for_text","text":"..."}).
function OcrBoxOverlay({ item }: { item: OCRItem }): JSX.Element {
  const [copied, setCopied] = useState(false);

  const onClick = useCallback(() => {
    void navigator.clipboard.writeText(item.text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      },
      () => { /* clipboard non dispo (http inverse ou perm denied) */ }
    );
  }, [item.text]);

  // Tailwind ne peut pas générer des classes dynamiques pour position/size
  // → on passe par style inline. Coords [0..1] → percent du parent.
  const style: React.CSSProperties = {
    left:   `${item.x * 100}%`,
    top:    `${item.y * 100}%`,
    width:  `${item.w * 100}%`,
    height: `${item.h * 100}%`,
  };

  const conf = Math.round(item.confidence * 100);
  // Couleur selon confidence : vert ≥ 0.9, jaune ≥ 0.5, rouge sinon. Aide à
  // repérer les détections fragiles qui pourraient faire fail un wait_for_text.
  const borderColor =
    conf >= 90 ? "border-emerald-400/80"
    : conf >= 50 ? "border-amber-400/80"
    : "border-red-400/80";
  const bgColor =
    conf >= 90 ? "bg-emerald-400/10"
    : conf >= 50 ? "bg-amber-400/10"
    : "bg-red-400/10";

  return (
    <div
      onClick={onClick}
      style={style}
      title={`"${item.text}" (confidence=${conf}%) — click to copy`}
      className={
        "absolute border-2 rounded-sm cursor-pointer pointer-events-auto transition-all " +
        borderColor + " " + bgColor + " hover:bg-violet-400/30 hover:border-violet-200"
      }
    >
      {/* Label texte au-dessus de la bbox. Pour les bboxes très petites
          (en haut de l'écran ou en bas) on inverse le label position pour
          éviter qu'il déborde hors du frame device. */}
      <span
        className={
          "absolute left-0 px-1 py-0 text-[10px] font-mono bg-black/85 text-white whitespace-nowrap rounded-sm " +
          (item.y > 0.05 ? "bottom-full mb-0.5" : "top-full mt-0.5")
        }
      >
        {copied ? "✓ copied" : item.text.slice(0, 40)}
      </span>
    </div>
  );
}

interface SystemKeyButtonProps {
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}

function SystemKeyButton({ icon, label, disabled, onClick }: SystemKeyButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? "Control WS déconnecté" : label}
      className={
        "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors " +
        (disabled
          ? "bg-ink-800 text-ink-600 cursor-not-allowed border border-ink-700"
          : "bg-ink-700 hover:bg-ink-600 text-white border border-ink-600")
      }
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
